<template>
    <div class="article-management">
        <!-- 使用通用头部组件 -->
        <AdminPageHeader title="文章管理" subtitle="管理博客文章">
            <template #actions>
                <a-space>
                    <a-button @click="loadArticles" :loading="isLoading">
                        <ReloadOutlined />
                        刷新
                    </a-button>
                    <a-button type="primary" @click="createArticle">
                        <PlusOutlined />
                        新建文章
                    </a-button>
                </a-space>
            </template>
        </AdminPageHeader>

        <div class="article-page-content">
            <!-- 加载状态 -->
            <a-spin v-if="isLoading" size="large" class="loading-spin">
                <div class="loading-content">
                    <p>正在加载文章列表...</p>
                </div>
            </a-spin>

            <!-- 错误状态 -->
            <a-result v-else-if="errorMessage" status="error" title="加载失败" :sub-title="errorMessage">
                <template #extra>
                    <a-button type="primary" @click="loadArticles">重试</a-button>
                </template>
            </a-result>

            <!-- 文章列表 -->
            <div v-else>
                <!-- 统计信息 -->
                <a-row :gutter="[16, 16]" class="stats-cards">
                    <a-col :xs="24" :sm="12" :md="6">
                        <a-card :bordered="false">
                            <a-statistic title="文章总数" :value="articles.length" />
                        </a-card>
                    </a-col>
                    <a-col :xs="24" :sm="12" :md="6">
                        <a-card :bordered="false">
                            <a-statistic title="已发布" :value="publishedCount" />
                        </a-card>
                    </a-col>
                    <a-col :xs="24" :sm="12" :md="6">
                        <a-card :bordered="false">
                            <a-statistic title="草稿" :value="draftCount" />
                        </a-card>
                    </a-col>
                    <a-col :xs="24" :sm="12" :md="6">
                        <a-card :bordered="false">
                            <a-statistic title="已删除" :value="deletedCount" />
                        </a-card>
                    </a-col>
                    <a-col :xs="24" :sm="12" :md="6">
                        <a-card :bordered="false">
                            <a-statistic title="总字数" :value="totalWords" />
                        </a-card>
                    </a-col>
                </a-row>

                <!-- 筛选器 -->
                <a-card class="filter-card" :bordered="false">
                    <a-space>
                        <span>状态筛选：</span>
                        <a-button-group>
                            <a-button :type="filterStatus === 'all' ? 'primary' : 'default'" @click="setFilter('all')">
                                全部
                            </a-button>
                            <a-button :type="filterStatus === 'published' ? 'primary' : 'default'"
                                @click="setFilter('published')">
                                已公开
                            </a-button>
                            <a-button :type="filterStatus === 'draft' ? 'primary' : 'default'"
                                @click="setFilter('draft')">
                                草稿箱
                            </a-button>
                            <a-button :type="filterStatus === 'private' ? 'primary' : 'default'"
                                @click="setFilter('private')">
                                不公开
                            </a-button>
                            <a-button :type="filterStatus === 'deleted' ? 'primary' : 'default'"
                                @click="setFilter('deleted')">
                                已删除
                            </a-button>
                        </a-button-group>
                        <a-divider type="vertical" />
                        <span>当前显示: {{ filteredArticles.length }} 篇文章</span>
                    </a-space>
                </a-card>

                <!-- 空状态 -->
                <a-empty v-if="filteredArticles.length === 0" :description="getEmptyDescription()">
                    <a-button v-if="filterStatus === 'all'" type="primary" @click="createArticle">
                        创建第一篇文章
                    </a-button>
                </a-empty>

                <!-- 文章列表 -->
                <a-card v-else class="article-list-card" :bordered="false">
                    <a-list :data-source="filteredArticles" item-layout="horizontal">
                        <template #renderItem="{ item: article }">
                            <a-list-item>
                                <template #actions>
                                    <!-- 查看文章（所有状态都可以查看） -->
                                    <a-tooltip title="查看文章">
                                        <a-button type="text" @click="viewArticle(article)">
                                            <EyeOutlined />
                                        </a-button>
                                    </a-tooltip>

                                    <!-- 编辑文章（非已删除状态可以编辑） -->
                                    <a-tooltip v-if="article.status !== 'deleted'" title="编辑文章">
                                        <a-button type="text" @click="editArticle(article)">
                                            <EditOutlined />
                                        </a-button>
                                    </a-tooltip>

                                    <!-- 已删除状态的操作按钮 -->
                                    <template v-if="article.status === 'deleted'">
                                        <a-tooltip title="恢复文章">
                                            <a-button type="text" @click="restoreArticle(article)">
                                                <UndoOutlined />
                                            </a-button>
                                        </a-tooltip>
                                        <a-tooltip title="永久删除">
                                            <a-button type="text" danger @click="permanentDeleteArticle(article)">
                                                <DeleteOutlined />
                                            </a-button>
                                        </a-tooltip>
                                    </template>

                                    <!-- 非已删除状态的删除按钮（逻辑删除） -->
                                    <a-tooltip v-else title="删除文章">
                                        <a-button type="text" danger @click="deleteArticle(article)">
                                            <DeleteOutlined />
                                        </a-button>
                                    </a-tooltip>
                                </template>

                                <a-list-item-meta>
                                    <template #title>
                                        <div class="article-title">
                                            <a-space class="article-title-content">
                                                <a-tag
                                                    v-if="article.status === 'published' && article.visibility === 'public'"
                                                    color="green" size="small">
                                                    已公开
                                                </a-tag>
                                                <a-tag
                                                    v-else-if="article.status === 'published' && article.visibility === 'private'"
                                                    color="orange" size="small">
                                                    不公开
                                                </a-tag>
                                                <a-tag v-else-if="article.status === 'draft'" color="blue" size="small">
                                                    草稿
                                                </a-tag>
                                                <a-tag v-else-if="article.status === 'deleted'" color="red"
                                                    size="small">
                                                    已删除
                                                </a-tag>
                                                <a-tag v-if="article.article_type === 'richtext'" color="purple"
                                                    size="small">
                                                    富文本
                                                </a-tag>
                                                <a-tag v-else color="geekblue" size="small">
                                                    Markdown
                                                </a-tag>
                                                <span class="article-title-text">{{ article.title }}</span>
                                            </a-space>
                                        </div>
                                    </template>
                                    <template #description>
                                        <div class="article-description">
                                            <p>{{ article.excerpt || '暂无摘要' }}</p>
                                            <div class="article-meta">
                                                <a-space>
                                                    <span>
                                                        <ClockCircleOutlined /> {{ formatDate(article.updated_at) }}
                                                    </span>
                                                    <span>
                                                        <FileTextOutlined /> {{ article.word_count || 0 }} 字
                                                    </span>
                                                    <span v-if="article.published_at">
                                                        <CalendarOutlined /> 发布于 {{ formatDate(article.published_at) }}
                                                    </span>
                                                </a-space>
                                            </div>
                                        </div>
                                    </template>
                                </a-list-item-meta>
                            </a-list-item>
                        </template>
                    </a-list>
                </a-card>
            </div>
        </div>

        <!-- 逻辑删除确认模态框 -->
        <a-modal :open="showDeleteDialog" title="确认删除" @ok="confirmDelete" @cancel="closeDeleteDialog"
            :confirm-loading="isDeleting" ok-text="删除到回收站" cancel-text="取消" ok-type="danger">
            <p>确定要删除文章 <strong>"{{ articleToDelete?.title }}"</strong> 吗？</p>
            <a-alert message="文章将被移动到回收站，可以在「已删除」中恢复。" type="info" show-icon class="delete-warning" />
        </a-modal>

        <!-- 永久删除确认模态框 -->
        <a-modal :open="showPermanentDeleteDialog" title="确认永久删除" @ok="confirmPermanentDelete"
            @cancel="closePermanentDeleteDialog" :confirm-loading="isPermanentDeleting" ok-text="永久删除" cancel-text="取消"
            ok-type="danger">
            <p>确定要永久删除文章 <strong>"{{ articleToPermanentDelete?.title }}"</strong> 吗？</p>
            <a-alert message="此操作不可撤销！文章的所有内容和评论都将被永久删除。" type="error" show-icon class="delete-warning" />
        </a-modal>

        <!-- 恢复文章确认模态框 -->
        <a-modal :open="showRestoreDialog" title="确认恢复" @ok="confirmRestore" @cancel="closeRestoreDialog"
            :confirm-loading="isRestoring" ok-text="恢复为草稿" cancel-text="取消" ok-type="primary">
            <p>确定要恢复文章 <strong>"{{ articleToRestore?.title }}"</strong> 吗？</p>
            <a-alert message="文章将被恢复为草稿状态，您可以继续编辑。" type="success" show-icon class="restore-info" />
        </a-modal>
    </div>
</template>

<script>
import { message } from 'ant-design-vue'
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ClockCircleOutlined,
    ReloadOutlined,
    FileTextOutlined,
    CalendarOutlined,
    EyeOutlined,
    UndoOutlined
} from '@ant-design/icons-vue'
import AdminPageHeader from '../../components/admin/AdminPageHeader.vue'
import supabaseArticleService from '../../services/supabaseArticleService.js'

export default {
    name: 'ArticleManagement',
    data() {
        return {
            articles: [],
            isLoading: true,
            errorMessage: null,
            showDeleteDialog: false,
            showPermanentDeleteDialog: false,
            showRestoreDialog: false,
            isDeleting: false,
            isPermanentDeleting: false,
            isRestoring: false,
            articleToDelete: null,
            articleToPermanentDelete: null,
            articleToRestore: null,
            filterStatus: 'all' // 'all', 'published', 'draft', 'private', 'deleted'
        }
    },
    computed: {
        publishedCount() {
            return this.articles.filter(article => article.status === 'published').length
        },
        draftCount() {
            return this.articles.filter(article => article.status === 'draft').length
        },
        deletedCount() {
            return this.articles.filter(article => article.status === 'deleted').length
        },
        totalWords() {
            return this.articles.reduce((total, article) => total + (article.word_count || 0), 0)
        },
        filteredArticles() {
            return this.articles.filter(article => {
                if (this.filterStatus === 'all') {
                    return true
                }
                if (this.filterStatus === 'published') {
                    return article.status === 'published' && article.visibility === 'public'
                }
                if (this.filterStatus === 'draft') {
                    return article.status === 'draft'
                }
                if (this.filterStatus === 'private') {
                    return article.status === 'published' && article.visibility === 'private'
                }
                if (this.filterStatus === 'deleted') {
                    return article.status === 'deleted'
                }
                return false
            })
        }
    },
    async mounted() {
        await this.loadArticles()
    },
    methods: {

        async loadArticles() {
            try {
                this.isLoading = true
                this.errorMessage = null

                const articles = await supabaseArticleService.listAll()
                this.articles = articles
            } catch (error) {
                console.error('加载文章失败:', error)
                this.errorMessage = '加载文章失败，请刷新重试'
            } finally {
                this.isLoading = false
            }
        },

        createArticle() {
            this.$router.push('/admin/article/new')
        },

        editArticle(article) {
            this.$router.push(`/admin/article/edit/${article.id}`)
        },

        deleteArticle(article) {
            this.articleToDelete = article
            this.showDeleteDialog = true
        },

        async confirmDelete() {
            try {
                this.isDeleting = true

                await supabaseArticleService.remove(this.articleToDelete.id)
                await this.loadArticles()

                this.closeDeleteDialog()
                message.success('文章删除成功！')
            } catch (error) {
                console.error('删除失败:', error)
                message.error(`删除失败：${error.message}`)
            } finally {
                this.isDeleting = false
            }
        },

        closeDeleteDialog() {
            this.showDeleteDialog = false
            this.articleToDelete = null
        },

        setFilter(status) {
            this.filterStatus = status
        },

        getEmptyDescription() {
            if (this.filterStatus === 'all') {
                return '还没有创建任何文章'
            }
            if (this.filterStatus === 'published') {
                return '还没有公开的文章'
            }
            if (this.filterStatus === 'draft') {
                return '还没有草稿的文章'
            }
            if (this.filterStatus === 'private') {
                return '还没有不公开的文章'
            }
            if (this.filterStatus === 'deleted') {
                return '还没有已删除的文章'
            }
            return '还没有创建任何文章'
        },

        formatDate(dateString) {
            return new Date(dateString).toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },

        // 恢复文章
        restoreArticle(article) {
            this.articleToRestore = article
            this.showRestoreDialog = true
        },

        async confirmRestore() {
            try {
                this.isRestoring = true
                await supabaseArticleService.restore(this.articleToRestore.id, 'draft')
                await this.loadArticles()
                this.closeRestoreDialog()
                message.success('文章恢复成功！')
            } catch (error) {
                console.error('恢复失败:', error)
                message.error(`恢复失败：${error.message}`)
            } finally {
                this.isRestoring = false
            }
        },

        closeRestoreDialog() {
            this.showRestoreDialog = false
            this.articleToRestore = null
        },

        // 永久删除文章
        permanentDeleteArticle(article) {
            this.articleToPermanentDelete = article
            this.showPermanentDeleteDialog = true
        },

        async confirmPermanentDelete() {
            try {
                this.isPermanentDeleting = true
                await supabaseArticleService.permanentDelete(this.articleToPermanentDelete.id)
                await this.loadArticles()
                this.closePermanentDeleteDialog()
                message.success('文章已永久删除！')
            } catch (error) {
                console.error('永久删除失败:', error)
                message.error(`永久删除失败：${error.message}`)
            } finally {
                this.isPermanentDeleting = false
            }
        },

        closePermanentDeleteDialog() {
            this.showPermanentDeleteDialog = false
            this.articleToPermanentDelete = null
        },

        viewArticle(article) {
            // 在新标签页中打开文章详情页
            const url = `/article/${article.id}`
            window.open(url, '_blank')
        }
    },

    components: {
        PlusOutlined,
        EditOutlined,
        DeleteOutlined,
        ClockCircleOutlined,
        ReloadOutlined,
        FileTextOutlined,
        CalendarOutlined,
        AdminPageHeader,
        EyeOutlined,
        UndoOutlined
    }
}
</script>

<style scoped>
.article-management {
    background: #f0f2f5;
}

.article-page-content {
    padding: 24px;
}

.loading-spin {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0;
}

.loading-content {
    text-align: center;
    margin-top: 16px;
}

.stats-cards {
    margin-bottom: 24px;
}

.filter-card {
    margin-bottom: 24px;
}

.article-list-card {
    margin-top: 24px;
}

/* 文章特有样式 */
.article-title {
    display: flex;
    align-items: center;
    width: 100%;
}

.article-title-content {
    display: flex;
    align-items: center;
    gap: 8px;
}

.article-title-text {
    font-size: 16px;
    font-weight: 600;
    color: #1890ff;
}

.article-description p {
    color: #666;
    font-size: 14px;
    line-height: 1.5;
    margin: 8px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.article-meta {
    margin-top: 8px;
    font-size: 12px;
    color: #999;
}

.article-meta span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.delete-warning {
    margin-top: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .article-page-content {
        padding: 16px;
    }

    .stats-cards {
        margin-bottom: 16px;
    }

    .filter-card {
        margin-bottom: 16px;
    }
}
</style>